import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { QrCode } from '@carouselgg/nexus/qr-code'
import { clockOffset, msUntilNextStep, passCode, passStep, type RotatingPass } from '@carouselgg/auth/pass-code'
import styles from './LivePass.module.css'

interface LivePassProps {
  pass: RotatingPass
  /** When the pass arrived (ms) — with the pass's server_time, corrects a clock that's off. */
  fetchedAt: number
  size: number
  label: string
}

/**
 * A pass that proves it's live, like a stadium ticket: the code changes every
 * period (the desk only accepts the current one), a bar sweeps over it, and a
 * clock ticks under it — none of which a screenshot can do. The web twin of
 * the app's LivePass; both derive the code with @carouselgg/auth/pass-code.
 */
export function LivePass({ pass, fetchedAt, size, label }: LivePassProps) {
  const offsetMs = useMemo(() => clockOffset(pass.server_time, fetchedAt) * 1000, [pass.server_time, fetchedAt])
  const [now, setNow] = useState(() => Date.now() + offsetMs)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() + offsetMs), 250)
    return () => clearInterval(id)
  }, [offsetMs])

  const step = passStep(Math.floor(now / 1000), pass.period_seconds)
  const code = useMemo(() => passCode(pass, step * pass.period_seconds), [pass, step])
  const remaining = msUntilNextStep(now, pass.period_seconds) / (pass.period_seconds * 1000)
  const clock = new Date(now).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })

  return (
    <div className={styles.live} style={{ '--live-size': `${size}px` } as CSSProperties}>
      <div className={styles.frame}>
        {/* Q correction: the sweeping bar hides a strip of modules as it passes. */}
        <QrCode value={code} size={size} ecc="Q" label={label} />
        <div className={styles.bar} aria-hidden="true" />
      </div>
      <div className={styles.track} aria-hidden="true">
        <div className={styles.fill} style={{ width: `${Math.round(remaining * 100)}%` }} />
      </div>
      <span className={styles.clock} aria-label={`Live pass, ${clock}`}>● LIVE · {clock}</span>
    </div>
  )
}
